import { FocusEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FormikErrors } from 'formik';
import { getGoogleAddressComponentsForCountry } from "@iddataweb/idw-common-ui/utils";
import { InternationalAddressCountryTypes } from "@iddataweb/idw-common-ui/enums";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { getCountryCode } from "@redux/selectors/verification-configuration-selectors";
import {
    getFormattedAddress,
    getInitialLocation
} from "@utils/address-util";
import { AutocompleteSubpremiseInput } from "./autocomplete-subpremise-input/autocomplete-subpremise-input";
import { InternationalAddressSelectInput } from "./international-address-select-input/international-address-select-input";
import { ManualInternationalAddressInputContainer } from "./manual-international-address-input/manual-international-address-input-container";
import styles from "./international-address-input-container.less";

type InternationalAddressInputContainerProps = {
    title: string;
    attribute: AttributeFormState;
    fieldName: string;
    fieldTouched: boolean;
    fieldError: string;
    fieldIsInvalid: boolean;
    fieldShouldBeHighlighted: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
}

export const InternationalAddressInputContainer = (props: InternationalAddressInputContainerProps) => {
    const {
        title,
        attribute,
        fieldName,
        fieldError,
        fieldTouched,
        fieldIsInvalid,
        fieldShouldBeHighlighted,
        setFieldValue,
        setFieldTouched,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const [addressObject, setAddressObject] = useState({ address: "", components: {} });
    const [hasGooglePlacesLoadError, setHasGooglePlacesLoadError] = useState(false);
    const [showManualAddressInput, setShowManualAddressInput] = useState(false);
    const [isEditingSubpremise, setIsEditingSubPremise] = useState(false);
    const [subpremise, setSubpremise] = useState("");

    const content = useSelector(getLanguageContent);
    const countryCode = useSelector(getCountryCode) as InternationalAddressCountryTypes;

    const resetAddressInput = async () => {
        setAddressObject(getInitialLocation(countryCode));
        await setFieldValue(fieldName, "");
        setFieldTouched(fieldName, true, true);
    };

    const onClickEnterAddressManually = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        await resetAddressInput();
        setIsEditingSubPremise(false);
        setShowManualAddressInput(true);
    };

    const onClickEnterAddressAutomatically = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        await resetAddressInput();
        setIsEditingSubPremise(false);
        setShowManualAddressInput(false);
    };

    const onClickEditSubpremise = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setIsEditingSubPremise(true);
    };

    const handleSetHasGooglePlacesLoadError = (hasError: boolean) => {
        if (hasError) {
            setHasGooglePlacesLoadError(true);
            setShowManualAddressInput(true);
        }
    };

    const googleAddressComponents = useMemo(() => getGoogleAddressComponentsForCountry(countryCode), [countryCode]);

    const initalizeFromInputData = async () => {
        const formattedAddress = getFormattedAddress(attribute.inputData, googleAddressComponents);
        setAddressObject({ address: formattedAddress, components: attribute.inputData });
        await setFieldValue(fieldName, formattedAddress);
    };

    useEffect(() => {
        const inputDataKeys = Object.keys(attribute.inputData);
        if (inputDataKeys.length > 0 && inputDataKeys.some(key => !!attribute.inputData[key])) {
            initalizeFromInputData();
        }
    }, []);

    useEffect(() => {
        const inputDataKeys = Object.keys(attribute.inputData);
        if (countryCode && (inputDataKeys.length === 0) || inputDataKeys.every(key => !attribute.inputData[key])) {
            setAddressObject(getInitialLocation(countryCode))
        }
    }, [countryCode]);

    useEffect(() => {
        onSetSubmitValue({ inputData: { ...addressObject.components } });
        setFieldValue(fieldName, addressObject.address);
    }, [addressObject.address, addressObject.components]);

    useEffect(() => {
        const inputData = { ...addressObject.components, subpremise };
        onSetSubmitValue({ inputData });
    }, [subpremise]);

    return (
        <div className={styles.internationalAddressInputContainer}>
            {!showManualAddressInput && (
                <>
                    <InternationalAddressSelectInput
                        googleAddressComponents={googleAddressComponents}
                        title={title}
                        attributeId={attribute.id}
                        required={!attribute.optional}
                        countryCode={countryCode}
                        fieldName={fieldName}
                        fieldError={fieldError}
                        fieldTouched={fieldTouched}
                        fieldIsInvalid={fieldIsInvalid}
                        fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                        addressObject={addressObject}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        setHasGooglePlacesLoadError={handleSetHasGooglePlacesLoadError}
                        setAddressObject={setAddressObject}
                    />
                    {isEditingSubpremise && (
                        <AutocompleteSubpremiseInput
                            subpremise={subpremise}
                            setSubpremise={setSubpremise}
                            setIsEditingSubpremise={setIsEditingSubPremise}
                        />
                    )}
                    {!isEditingSubpremise && addressObject.address && (
                        <div className={styles.anchorDiv}>
                            <a
                                id={`attr${attribute.id}-set-subpremise`}
                                href={""}
                                onClick={onClickEditSubpremise}
                            >
                                {content.form.internationalAddress.addSubPremise}
                            </a>
                        </div>
                    )}
                    <div className={styles.linkContainer}>
                        <a
                            href={""}
                            className={styles.manualLink}
                            onClick={onClickEnterAddressManually}>
                            {content.form.internationalAddress.enterAddressManually}
                        </a>
                    </div>
                </>
            )}
            {showManualAddressInput && (
                <>
                    {!hasGooglePlacesLoadError && (
                        <div className={styles.linkContainer}>
                            <a
                                href={""}
                                className={styles.manualLink}
                                onClick={onClickEnterAddressAutomatically}>
                                {content.form.internationalAddress.enterAddressAutomatically}
                            </a>
                        </div>
                    )}
                    <ManualInternationalAddressInputContainer
                        googleAddressComponents={googleAddressComponents}
                        countryCode={countryCode}
                        attribute={attribute}
                        isSubmitting={isSubmitting}
                        fieldName={fieldName}
                        onBlur={onBlur}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        onSetSubmitValue={onSetSubmitValue}
                        setAddressObject={setAddressObject}
                    />
                </>
            )}
        </div>
    )
};